import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import LabeledTextField from '../CustomTextField/LabeledTextField';
import DragDrop from '../ImageUploader/DragDrop';
import { Box, CircularProgress, Collapse, Divider, SelectChangeEvent } from '@mui/material';
import CustomButton from '../CustomButton/CustomButton';
import { useAppSelector } from '@src/store/hooks';
import ImageThumnail from '../ImagePreview/ImageThumnail';
import {
	cleanObject,
	generateUniqueID,
	validateFilePixelsAndSize,
	validateFileUpload,
} from '@src/helpers/Helpers';
import LabeledSelectField, { Option } from '../CustomSelect/LabeledSelectField';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { error } from 'console';
import { useNavigate, useParams } from 'react-router-dom';
import { priceRegex } from '@src/pages/Catalogs';
import { CustomAlertType, FileError, PartOptions } from '@src/pages/Products';
import { Image } from '@src/interfaces/type';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomPermission from '../Permissions/CustomPermission';
import { LockIcon } from '../Icons';
import { IsMobileWidth } from '../MediaQueryUtils';
import PageHeaderMobile from '../PageHeader/PageHeaderMobile';
import SearchBar from '../SearchBar';
import { use } from 'i18next';

const PageContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: calc(100% - 144px);
	overflow: scroll;
`;
const Container = styled.div`
	display: flex;
	justify-content: space-around;
	width: 100%;
	height: calc(100% - 83px);
	padding-top: 30px;
	@media screen and (max-width: 600px) {
		overflow: auto;
		flex-grow: 1;
		height: auto;
		margin-bottom: 72px;
		padding-top: 10px;
	}
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
	height: 100%;
	width: 100%;
	@media screen and (max-width: 600px) {
		padding: 24px;
	}
`;
const FieldsContainer = styled.div`
	display: flex;
	gap: 28px;
	@media screen and (max-width: 600px) {
		flex-direction: column;
	}
`;

const ImageContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 5px;
`;
const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
`;

const BottomContainer = styled.div<{ isDrawerOpen: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-around;
	width: ${(props) => (props.isDrawerOpen ? 'calc(100% - 240px)' : 'calc(100vw - 64px)')};
	padding-right: 10px;
	height: 83px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	position: fixed;
	bottom: 0;
	transition: width 0.3s ease;
	@media screen and (max-width: 800px) {
		width: 100%;
	}
`;

const ButtonContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 15px;
	width: 658px;
	@media screen and (max-width: 800px) {
		justify-content: center;
	}
`;

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#filter-container {
		display: flex;
		gap: 10px;
		padding-left: 16px;
		padding-right: 16px;
		padding-bottom: 16px;
	}
`;

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 50;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 0;
`;

type Props = {
	displayError: (data: string) => void;
	categoryOptions: Option[];
	attributeOptions: Option[];
	taxClassesOptions: Option[];
	stockStatusOptions: Option[];
	setInvalidFilesError: (data: FileError[]) => void;
	totalPages: number;
	setOperation: (data: string) => void;
	setPage: (data: number) => void;
	setApiCall: (data: boolean) => void;
	setAlert: (data: CustomAlertType) => void;
	partOptions: PartOptions | null;
};
const ProductCreation: FC<Props> = ({
	displayError,
	categoryOptions,
	attributeOptions,
	taxClassesOptions,
	stockStatusOptions,
	setInvalidFilesError,
	totalPages,
	setOperation,
	setPage,
	setApiCall,
	setAlert,
	partOptions,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const { isDrawerOpen } = useAppSelector((state) => state.UI);
	const navigate = useNavigate();
	const pixels = {
		height: 1000,
		width: 1000,
	};
	const { id } = useParams();

	const [loading, setLoading] = useState(false);
	//Options
	const [categories, setCategories] = useState([]);
	//Form States
	const [productId, setProductId] = useState<string>('');
	const [price, setPrice] = useState<string>('');
	const [productName, setProductName] = useState<string>('');
	const [category, setCategory] = useState<string>('');
	const [subCategory, setSubCategory] = useState<string>('');
	const [description, setDescription] = useState<string>('');
	const [model, setModel] = useState<string>('');
	const [sku, setSKU] = useState<string>('');
	const [tax, setTax] = useState<string>('');
	const [stockStatus, setStockStatus] = useState<string>('');
	const [quantity, setQuantity] = useState<string>('');
	const [attributeType, setAttributeType] = useState<string>('');
	const [attributeValue, setAttributeValue] = useState<string>('');
	const [subCategoryOptions, setSubCategoryOptions] = useState<Option[]>([]);
	const [unit, setUnit] = useState<string>('');

	// New fields
	const [brand, setBrand] = useState('');
	const [partNumber, setPartNumber] = useState('');
	const [partDimensionId, setPartDimensionId] = useState('');
	const [partDiameterId, setPartDiameterId] = useState('');
	const [partWeight, setPartWeight] = useState('');
	const [machineBrand, setMachinBrand] = useState('');
	const [partQualityId, setPartQualityId] = useState('');
	const [partCompositionId, setPartCompositionId] = useState('');
	const [partDesignId, setPartDesignId] = useState('');
	const [partPerformanceId, setPartPerformanceId] = useState('');
	const [partGroupCategory, setPartGroupCategory] = useState('');
	const [partOwnerShip, setPartOwnerShip] = useState('');

	//file-upload
	const [files, setFiles] = useState<File[]>([]);
	const [images, setImages] = useState<Image[]>([]);

	const isMobileWidth = IsMobileWidth();

	//Permission
	const checkPermission = usePermission();
	const canCreateProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.CREATE, id);

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const fieldsStyle1 = { width: !isMobileWidth ? '658px' : '90vw', height: '36px' };
	const fieldsStyle2 = { width: !isMobileWidth ? '315px' : '90vw', height: '36px' };

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const filesData = event?.target?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];
		let errorMessages: FileError[] = [];

		if (imageCount <= 3) {
			if (filesData) {
				for (let i = 0; i < filesData.length; i++) {
					const file = filesData[i];
					const validFile = await validateFilePixelsAndSize(file, pixels);

					if (validFile?.toLowerCase() === 'allowed') {
						validFiles.push(file);
						const fileURL = URL.createObjectURL(file);
						imagesData.push({ id: generateUniqueID(), url: fileURL, hover: false });
					} else {
						errorMessages.push({ name: file?.name, message: validFile });
					}
				}
			}
			setFiles([...files, ...validFiles]);
			setImages(imagesData);

			setInvalidFilesError(errorMessages);
		} else {
			displayError('Maximum of 3 images allowed');
		}
	};

	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const filesData = event.dataTransfer?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];
		let errorMessages: FileError[] = [];

		if (imageCount <= 3) {
			if (filesData) {
				for (let i = 0; i < filesData.length; i++) {
					const file = filesData[i];
					const validFile = await validateFilePixelsAndSize(file, pixels);

					if (validFile?.toLowerCase() === 'allowed') {
						validFiles.push(file);
						const fileURL = URL.createObjectURL(file);
						imagesData.push({ id: generateUniqueID(), url: fileURL, hover: false });
					} else {
						errorMessages.push({ name: file?.name, message: validFile });
					}
				}
			}
			setFiles([...files, ...validFiles]);
			setImages(imagesData);

			setInvalidFilesError(errorMessages);
		} else {
			displayError('Maximum of 3 images allowed');
		}
	};

	const resetAllFields = () => {
		setProductName('');
		setCategory('');
		setPrice('');
		setSubCategory('');
		setModel('');
		setSKU('');
		setStockStatus('');
		setTax('');
		setQuantity('');
		setAttributeType('');
		setAttributeValue('');
		setDescription('');
		setImages([]);
	};

	const disableCreateBtn = () => {
		if (
			!productName ||
			!category ||
			!price ||
			!quantity ||
			!model ||
			!machineBrand ||
			!sku ||
			// !partCompositionId ||
			// !partDiameterId ||
			// !partDesignId ||
			// !partDimensionId ||
			// !partGroupCategory ||
			// !partNumber ||
			// !subCategory ||
			// !partWeight ||
			// !partOwnerShip ||
			// !partQualityId ||
			// !partPerformanceId ||
			!brand ||
			loading
		) {
			return true;
		}
		return false;
	};

	const handleClose = () => {};

	const handleSelectCategory = (event: SelectChangeEvent<unknown>) => {
		const selectedValue = event.target.value as string;
		console.log(selectedValue);
		setCategory(selectedValue);
		setSubCategory('');
	};

	const uploadImages = () => {
		const uploadPromises = [];

		for (let i = 0; i < files?.length; i++) {
			const file = files[i];
			const formData = new FormData();
			formData.append('file', file);

			const uploadPromise: any = new Promise((resolve, reject) => {
				apiService?.uploadProductImage(
					formData,
					(response: AxiosResponse) => {
						resolve(response?.data?.data?.url);
					},
					(error: AxiosError) => {
						displayError('Something went wrong');
						reject(error);
					}
				);
			});

			uploadPromises.push(uploadPromise);
		}

		return Promise.all(uploadPromises);
	};

	const createProduct = async () => {
		setLoading(true);
		const imagesUrl = await uploadImages();
		const prodcutFormData = cleanObject({
			name: productName,
			model: model,
			category: category,
			subCategory: subCategory,
			price: price,
			quantity: quantity,
			stockStatusId: stockStatus,
			taxClassId: tax,
			attributeId: attributeType,
			attribute: attributeValue,
			sku: sku,
			images: imagesUrl,
			description: description,
			machineBrand: machineBrand,
			machineModel: model,
			groupCategory: partGroupCategory,
			brand: brand,
			partNumber: partNumber,
			partComposition: partCompositionId,
			partDesign: partDesignId,
			partDiameter: partDiameterId,
			partDimension: partDimensionId,
			partOwnership: partOwnerShip,
			partPerformance: partPerformanceId,
			partQuality: partQualityId,
			partWeight: partWeight,
		});

		apiService?.createProduct(
			prodcutFormData,
			(response: AxiosResponse) => {
				setAlert({ message: 'Product Added Successfully', severity: 'success' });
				setApiCall(true);
				setOperation('add');
				setLoading(false);
				setFiles([]);
				setPage(totalPages);
				resetAllFields();
				navigate(-1);
			},
			(error: AxiosError) => {
				console.log(error);
				displayError('Something went wrong');
				setLoading(false);
				setFiles([]);
			}
		);
	};

	useEffect(() => {
		if (category) {
			apiService?.getSubCategories(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((option: any) => {
						return { value: option?.name, id: option?.sub_category_id };
					});
					setSubCategoryOptions(options);
				},
				(error: AxiosError) => {},
				category
			);
		}
	}, [category]);

	useEffect(() => {
		if (partGroupCategory) {
			apiService?.getCategories(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((option: any) => {
						return { value: option?.name, id: option?.catgeory_id };
					});
					setCategories(options);
				},
				(error: AxiosError) => {},
				partGroupCategory
			);
		}
	}, [partGroupCategory]);

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	// if (isMobileWidth) {
	// 	return (
	// 		<StyledMobileContainer>
	// 			<PageHeaderMobile
	// 				title="Create Product"
	// 				onClick={() => {
	// 					navigate(-1);
	// 				}}
	// 			/>
	// 			{canCreateProduct ? (
	// 				<>
	// 					<Container>
	// 						<StyledForm>
	// 							<LabeledTextField
	// 								label="Part Name"
	// 								value={productName}
	// 								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
	// 									setProductName(e?.target?.value as string)
	// 								}
	// 								placeholder="Avacado"
	// 								styles={fieldsStyle1}
	// 								required
	// 							/>
	// 							<LabeledSelectField
	// 								label="Part Category"
	// 								value={category}
	// 								handleChange={(e) => handleSelectCategory(e)}
	// 								placeholder="e.g meat"
	// 								styles={fieldsStyle1}
	// 								options={categoryOptions}
	// 								required
	// 							/>
	// 							<LabeledTextField
	// 								label="Price (INR)"
	// 								value={price}
	// 								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
	// 									if (priceRegex.test(e?.target?.value)) {
	// 										setPrice(e?.target?.value as string);
	// 									}
	// 								}}
	// 								placeholder="e.g 20.00"
	// 								styles={fieldsStyle1}
	// 								required
	// 							/>
	// 							<FieldsContainer>
	// 								<LabeledSelectField
	// 									label="Part Sub Category"
	// 									value={subCategory}
	// 									handleChange={(event: SelectChangeEvent<unknown>) => {
	// 										const selectedValue = event.target.value as string;
	// 										setSubCategory(selectedValue);
	// 									}}
	// 									disable={!subCategoryOptions?.length}
	// 									placeholder="e.g chicken"
	// 									styles={fieldsStyle2}
	// 									options={subCategoryOptions}
	// 								/>
	// 								<LabeledTextField
	// 									label="Model"
	// 									value={model}
	// 									onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
	// 										setModel(e?.target?.value as string)
	// 									}
	// 									placeholder="01"
	// 									styles={fieldsStyle2}
	// 									required
	// 								/>
	// 							</FieldsContainer>
	// 							<FieldsContainer>
	// 								<LabeledTextField
	// 									label="SKU"
	// 									value={sku}
	// 									onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
	// 										setSKU(e?.target?.value as string)
	// 									}
	// 									placeholder="ns-001"
	// 									styles={fieldsStyle2}
	// 								/>

	// 								<LabeledSelectField
	// 									label="Stock Status"
	// 									value={stockStatus}
	// 									handleChange={(event: SelectChangeEvent<unknown>) => {
	// 										setStockStatus(event?.target?.value as string);
	// 									}}
	// 									//disable={!subCategoryOptions?.length}
	// 									options={stockStatusOptions}
	// 									placeholder="eg 18%"
	// 									styles={fieldsStyle2}
	// 									required
	// 								/>
	// 							</FieldsContainer>
	// 							<FieldsContainer>
	// 								<LabeledSelectField
	// 									label="Tax"
	// 									value={tax}
	// 									handleChange={(event: SelectChangeEvent<unknown>) => {
	// 										setTax(event?.target?.value as string);
	// 									}}
	// 									//disable={!subCategoryOptions?.length}
	// 									options={taxClassesOptions}
	// 									placeholder="eg 18%"
	// 									styles={fieldsStyle2}
	// 									required
	// 								/>
	// 								<LabeledTextField
	// 									label="Quantity"
	// 									value={quantity}
	// 									onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
	// 										const inputValue = e?.target?.value as string;
	// 										if (/^[1-9]?\d*$/.test(inputValue)) {
	// 											setQuantity(inputValue);
	// 										}
	// 									}}
	// 									placeholder="e.g 2qt"
	// 									styles={fieldsStyle2}
	// 									required
	// 								/>
	// 							</FieldsContainer>
	// 							<FieldsContainer>
	// 								<LabeledSelectField
	// 									label="Attribute Type"
	// 									value={attributeType}
	// 									handleChange={(e: SelectChangeEvent<unknown>) => {
	// 										setAttributeType(e?.target?.value as string);
	// 									}}
	// 									placeholder="eg 18%"
	// 									styles={fieldsStyle2}
	// 									options={attributeOptions}
	// 									required
	// 								/>
	// 								<LabeledTextField
	// 									label="Attribute Value"
	// 									value={attributeValue}
	// 									onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
	// 										setAttributeValue(e?.target?.value as string)
	// 									}
	// 									placeholder="e.g 2qt"
	// 									styles={fieldsStyle2}
	// 									required
	// 								/>
	// 							</FieldsContainer>
	// 							<LabeledTextField
	// 								label="Product Description"
	// 								value={description}
	// 								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
	// 									setDescription(e?.target?.value as string)
	// 								}
	// 								multiline
	// 								rows={5}
	// 								placeholder="Type Here"
	// 								styles={fieldsStyle1}
	// 							/>
	// 							<ImageContainer>
	// 								<StyledLabel>Attach files</StyledLabel>
	// 								{images?.length ? (
	// 									<ImageThumnail
	// 										images={images}
	// 										setImages={setImages}
	// 										setFiles={setFiles}
	// 										handleFileChange={handleFileChange}
	// 										windowDimensions={windowDimensions}
	// 									/>
	// 								) : (
	// 									<DragDrop
	// 										handleFileChange={handleFileChange}
	// 										handleFileDrop={handleFileDrop}
	// 										text="Select images to upload"
	// 										info="Upload up to 3 images: PNG/JPG/JPEG, dimensions: 1000x1000 px and a max file size: 2MB"
	// 										multiple
	// 									/>
	// 								)}
	// 							</ImageContainer>
	// 						</StyledForm>
	// 					</Container>
	// 					<StyledBottomBtnContainer>
	// 						<div
	// 							style={{
	// 								display: 'flex',
	// 								alignItems: 'center',
	// 								justifyContent: 'center',
	// 								width: '100%',
	// 							}}
	// 						>
	// 							<CustomButton
	// 								type="submit"
	// 								title={
	// 									loading ? (
	// 										<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
	// 									) : (
	// 										'Create'
	// 									)
	// 								}
	// 								handleClick={createProduct}
	// 								disabled={disableCreateBtn()}
	// 								styles={{
	// 									width: '320px',
	// 									height: '40px',
	// 									borderRadius: '4px',
	// 									padding: '7px 47px 7px 47px',
	// 									background: 'var(--color-primary)',
	// 									color: 'var(--default-text-color)',
	// 								}}
	// 							/>
	// 						</div>
	// 					</StyledBottomBtnContainer>
	// 				</>
	// 			) : (
	// 				<CustomPermission
	// 					icon={<LockIcon />}
	// 					title="We are sorry..."
	// 					paragraph="You do not have permission to create product. Contact
	// 			an administrator to get permission."
	// 				/>
	// 			)}
	// 		</StyledMobileContainer>
	// 	);
	// }
	return (
		<PageContainer>
			{canCreateProduct ? (
				<>
					<Container>
						<form
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '20px',
								height: '100%',
							}}
						>
							<LabeledTextField
								label="Part Name"
								value={productName}
								onChange={(e: any) => setProductName(e.target.value)}
								placeholder="Name"
								styles={fieldsStyle1}
								required
							/>

							<LabeledTextField
								label="Price (INR)"
								value={price}
								onChange={(e: any) => {
									if (priceRegex.test(e.target.value)) {
										setPrice(e.target.value);
									}
								}}
								placeholder="e.g 20.00"
								styles={fieldsStyle1}
								required
							/>

							<LabeledSelectField
								label="Part Group Category"
								value={partGroupCategory}
								handleChange={(e: any) => setPartGroupCategory(e.target.value)}
								placeholder="Part Group Category"
								styles={fieldsStyle1}
								options={partOptions?.groupOptions}
								required
							/>
							<FieldsContainer>
								<LabeledSelectField
									label="Part Category"
									value={category}
									handleChange={(e) => handleSelectCategory(e)}
									placeholder="e.g Category"
									styles={fieldsStyle2}
									options={categories}
									required
								/>
								<LabeledSelectField
									label="Part Sub Category"
									value={subCategory}
									handleChange={(event: any) => setSubCategory(event?.target?.value)}
									disable={!subCategoryOptions.length}
									placeholder="e.g Sub Category"
									styles={fieldsStyle2}
									options={subCategoryOptions}
									// required
								/>
								{/* <LabeledTextField
									label="Model"
									value={model}
									onChange={(e: any) => setModel(e.target.value)}
									placeholder="01"
									styles={fieldsStyle2}
									required
								/> */}
							</FieldsContainer>
							<FieldsContainer>
								<LabeledTextField
									label="SKU"
									value={sku}
									onChange={(e: any) => setSKU(e.target.value)}
									placeholder="ns-001"
									styles={fieldsStyle2}
									required
								/>
								{/* <LabeledSelectField
									label="Stock Status"
									value={stockStatus}
									handleChange={(event: any) => setStockStatus(event.target.value)}
									options={stockStatusOptions}
									placeholder="eg 18%"
									styles={fieldsStyle2}
									required
								/> */}
								<LabeledTextField
									label="Quantity"
									value={quantity}
									onChange={(e: any) => {
										if (/^[1-9]?\d*$/.test(e.target.value)) {
											setQuantity(e.target.value);
										}
									}}
									placeholder="e.g 2qt"
									styles={fieldsStyle2}
									required
								/>
							</FieldsContainer>
							{/* <FieldsContainer>
								<LabeledSelectField
									label="Tax"
									value={tax}
									handleChange={(event: any) => setTax(event.target.value)}
									options={taxClassesOptions}
									placeholder="eg 18%"
									styles={fieldsStyle2}
									required
								/>
		
							</FieldsContainer> */}
							{/* <FieldsContainer>
								<LabeledSelectField
									label="Attribute Type"
									value={attributeType}
									handleChange={(e: any) => setAttributeType(e.target.value)}
									placeholder="eg 18%"
									styles={fieldsStyle2}
									options={attributeOptions}
									required
								/>
								<LabeledTextField
									label="Attribute Value"
									value={attributeValue}
									onChange={(e: any) => setAttributeValue(e.target.value)}
									placeholder="e.g 2qt"
									styles={fieldsStyle2}
									required
								/>
							</FieldsContainer> */}

							<FieldsContainer>
								<LabeledTextField
									label="Part Number"
									value={partNumber}
									onChange={(e: any) => setPartNumber(e.target.value)}
									placeholder="Part Number"
									styles={fieldsStyle2}
									// required
								/>

								<LabeledSelectField
									label="Brand"
									value={brand}
									handleChange={(e: any) => setBrand(e.target.value)}
									placeholder="Brand"
									styles={fieldsStyle2}
									options={partOptions?.brandOptions}
									required
								/>
							</FieldsContainer>

							{/* New Fields */}
							<FieldsContainer>
								{' '}
								<LabeledSelectField
									label="Machine Brand"
									value={machineBrand}
									handleChange={(e: any) => setMachinBrand(e.target.value)}
									placeholder="Machine Brand"
									styles={fieldsStyle2}
									options={partOptions?.manufacturerOptions}
									required
								/>
								<LabeledSelectField
									label="Model"
									value={model}
									handleChange={(e: any) => setModel(e.target.value)}
									placeholder="Model"
									styles={fieldsStyle2}
									options={partOptions?.modelOptions}
									required
								/>
							</FieldsContainer>
							<FieldsContainer>
								{' '}
								<LabeledSelectField
									label="Part Dimension ID"
									value={partDimensionId}
									handleChange={(e: any) => setPartDimensionId(e.target.value)}
									placeholder="Part Dimension ID"
									styles={fieldsStyle2}
									options={partOptions?.dimensionOptions}
									// required
								/>
								<LabeledSelectField
									label="Part Diameter ID"
									value={partDiameterId}
									handleChange={(e: any) => setPartDiameterId(e.target.value)}
									placeholder="Part Diameter ID"
									styles={fieldsStyle2}
									options={partOptions?.diameterOptions}
									// required
								/>
							</FieldsContainer>
							<FieldsContainer>
								{' '}
								<LabeledSelectField
									label="Part Weight"
									value={partWeight}
									handleChange={(e: any) => setPartWeight(e.target.value)}
									placeholder="Part Weight"
									styles={fieldsStyle2}
									options={partOptions?.weightOptions}
									// required
								/>
								<LabeledSelectField
									label="Part Quality ID"
									value={partQualityId}
									handleChange={(e: any) => setPartQualityId(e.target.value)}
									placeholder="Part Quality ID"
									styles={fieldsStyle2}
									options={partOptions?.qualityOptions}
									// required
								/>
							</FieldsContainer>
							<FieldsContainer>
								{' '}
								<LabeledSelectField
									label="Part Composition ID"
									value={partCompositionId}
									handleChange={(e: any) => setPartCompositionId(e.target.value)}
									placeholder="Part Composition ID"
									styles={fieldsStyle2}
									options={partOptions?.compositionOptions}
									// required
								/>
								<LabeledSelectField
									label="Part Design ID"
									value={partDesignId}
									handleChange={(e: any) => setPartDesignId(e.target.value)}
									placeholder="Part Design ID"
									styles={fieldsStyle2}
									options={partOptions?.designOptions}
									// required
								/>
							</FieldsContainer>

							<FieldsContainer>
								{' '}
								<LabeledSelectField
									label="Part Performance ID"
									value={partPerformanceId}
									handleChange={(e: any) => setPartPerformanceId(e.target.value)}
									placeholder="Part Performance ID"
									styles={fieldsStyle2}
									options={partOptions?.performanceOptions}
									// required
								/>
								<LabeledSelectField
									label="Part Ownership"
									value={partOwnerShip}
									handleChange={(e: any) => setPartOwnerShip(e.target.value)}
									placeholder="Part Group Category"
									styles={fieldsStyle2}
									options={partOptions?.ownershipOptions}
									// required
								/>
							</FieldsContainer>

							<LabeledTextField
								label="Product Description"
								value={description}
								onChange={(e: any) => setDescription(e.target.value)}
								multiline
								rows={5}
								placeholder="Type Here"
								styles={fieldsStyle1}
							/>
							<ImageContainer>
								<StyledLabel>Attach files</StyledLabel>
								{images.length ? (
									<ImageThumnail
										images={images}
										setImages={setImages}
										setFiles={setFiles}
										handleFileChange={handleFileChange}
										windowDimensions={windowDimensions}
									/>
								) : (
									<DragDrop
										handleFileChange={handleFileChange}
										handleFileDrop={handleFileDrop}
										text="Select images to upload"
										info="Upload up to 3 images: PNG/JPG/JPEG, dimensions: 1000x1000 px and a max file size: 2MB"
										multiple
									/>
								)}
							</ImageContainer>
						</form>
					</Container>
					<BottomContainer isDrawerOpen={isDrawerOpen}>
						<ButtonContainer>
							<CustomButton
								type="submit"
								title={
									loading ? (
										<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
									) : (
										'Create'
									)
								}
								handleClick={createProduct}
								disabled={disableCreateBtn()}
								styles={{
									width: windowDimensions.width > 800 ? '143px' : '328px',
									height: '36px',
									borderRadius: '4px',
									padding: '7px 47px 7px 47px',
									background: 'var(--color-primary)',
									color: 'var(--default-text-color)',
								}}
							/>
						</ButtonContainer>
					</BottomContainer>
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph="You do not have permission to create product. Contact an administrator to get permission."
				/>
			)}
		</PageContainer>
	);
};

export default ProductCreation;
